import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { Badge, deliveryTone, rsvpLabel, rsvpTone } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { useToast } from '../ui/Toast';

interface Attendee {
  id: string;
  email: string;
  name?: string | null;
  rsvp: string;
  rsvpAt?: string | null;
  sendCount: number;
  lastStatus?: string | null;
}

interface Delivery {
  id: string;
  toEmail: string;
  status: string;
  method: string;
  messageId?: string | null;
  smtpResponse?: string | null;
  errorMessage?: string | null;
  queuedAt: string;
  sentAt?: string | null;
  failedAt?: string | null;
  attemptCount: number;
  sequence?: number | null;
}

interface Reminder {
  id: string;
  minutesBefore: number;
  channel: 'NOTIFICATION' | 'EMAIL';
  sentAt?: string | null;
}

interface EventDetail {
  id: string;
  uid: string;
  sequence: number;
  summary: string;
  description?: string | null;
  location?: string | null;
  startsAt: string;
  endsAt: string;
  timezone: string;
  allDay: boolean;
  rrule?: string | null;
  status: string;
  calendar: { id: string; name: string; color: string };
  attendees: Attendee[];
  reminders: Reminder[];
  deliveries: Delivery[];
}

interface HistoryItem {
  id: string;
  action: string;
  message: string;
  createdAt: string;
  user?: { email: string; name?: string | null } | null;
}

export function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [ingestOpen, setIngestOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [ingestText, setIngestText] = useState('');
  const [editForm, setEditForm] = useState({
    summary: '',
    description: '',
    location: '',
    startsAt: '',
    endsAt: '',
    timezone: 'Europe/Berlin',
    allDay: false,
    status: 'CONFIRMED',
    recurrence: 'NONE',
    sendInvitations: true,
  });
  const [attendeesInput, setAttendeesInput] = useState('');
  const [remindersInput, setRemindersInput] = useState('');
  const toast = useToast();

  const load = () => Promise.all([
    api.get<EventDetail>(`/api/events/${id}`).then(setEvent),
    api.get<HistoryItem[]>(`/api/events/${id}/history`).then(setHistory),
  ]);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!event) return <div className="text-ink-secondary">Lade …</div>;

  const openEdit = () => {
    setEditForm({
      summary: event.summary,
      description: event.description ?? '',
      location: event.location ?? '',
      startsAt: toLocalInput(new Date(event.startsAt)),
      endsAt: toLocalInput(new Date(event.endsAt)),
      timezone: event.timezone,
      allDay: event.allDay,
      status: event.status,
      recurrence: rruleToRecurrence(event.rrule),
      sendInvitations: true,
    });
    setAttendeesInput(event.attendees.map((a) => (a.name ? `${a.name} <${a.email}>` : a.email)).join('\n'));
    setRemindersInput(remindersToInput(event.reminders));
    setEditOpen(true);
  };

  const ingest = async () => {
    try {
      const res = await api.post<{ updatedAttendees: { email: string; status: string }[] }>(
        '/api/events/reply',
        { ics: ingestText },
      );
      toast.success(`${res.updatedAttendees.length} Antwort(en) verarbeitet`);
      setIngestOpen(false);
      setIngestText('');
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Fehler beim Einspielen');
    }
  };

  const resend = async (method: 'REQUEST' | 'REMINDER' | 'CANCEL') => {
    try {
      await api.post(`/api/events/${event.id}/invitations`, { method });
      toast.success(method === 'REMINDER' ? 'Erinnerung eingereiht' : method === 'CANCEL' ? 'Absage eingereiht' : 'Einladung eingereiht');
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Versand konnte nicht gestartet werden');
    }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/api/events/${event.id}`, {
        summary: editForm.summary,
        description: editForm.description || null,
        location: editForm.location || null,
        startsAt: new Date(editForm.startsAt).toISOString(),
        endsAt: new Date(editForm.endsAt).toISOString(),
        timezone: editForm.timezone,
        allDay: editForm.allDay,
        status: editForm.status,
        rrule: recurrenceToRrule(editForm.recurrence),
        attendees: parseAttendees(attendeesInput),
        reminders: parseReminders(remindersInput),
        sendInvitations: editForm.sendInvitations,
      });
      toast.success('Termin gespeichert');
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Termin konnte nicht gespeichert werden');
    }
  };

  const deleteEvent = async () => {
    try {
      await api.delete(`/api/events/${event.id}`);
      toast.success('Termin geloescht');
      navigate('/events');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Termin konnte nicht geloescht werden');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="card">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-meta uppercase tracking-[0.16em] text-ink-muted">{event.calendar.name}</div>
            <h1 className="text-h3 mt-1">{event.summary}</h1>
            <div className="text-body text-ink-secondary mt-2">
              {new Date(event.startsAt).toLocaleString('de-DE')} – {new Date(event.endsAt).toLocaleString('de-DE')}
              <span className="text-ink-muted"> · {event.timezone}</span>
            </div>
            {event.location && <div className="text-body text-ink-secondary mt-1">Ort: {event.location}</div>}
            {event.description && (
              <div className="text-body text-ink-secondary mt-3 whitespace-pre-wrap">{event.description}</div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2 text-meta">
            <Badge tone={event.status === 'CANCELLED' ? 'danger' : 'info'}>{event.status.toLowerCase()}</Badge>
            <div className="font-mono text-ink-muted text-meta">UID {event.uid}</div>
            <div className="font-mono text-ink-muted text-meta">SEQ {event.sequence}</div>
            <div className="flex gap-2 mt-2">
              <button className="btn btn-secondary" onClick={openEdit}>Bearbeiten</button>
              <button className="btn btn-danger" onClick={deleteEvent}>Loeschen</button>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <h2 className="text-h4 mb-4">Erinnerungen</h2>
        <div className="flex flex-wrap gap-2">
          {event.reminders.map((reminder) => (
            <span key={reminder.id} className={`badge ${reminder.sentAt ? 'badge-success' : 'badge-muted'}`}>
              {formatReminder(reminder.minutesBefore)} · {reminder.channel === 'EMAIL' ? 'Mail' : 'Push/In-App'}
            </span>
          ))}
          {event.reminders.length === 0 && <span className="text-small text-ink-muted">Keine Erinnerungen.</span>}
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-h4">Teilnehmer & RSVP</h2>
          <div className="flex gap-2">
            <button className="btn btn-secondary" onClick={() => resend('REQUEST')} disabled={event.attendees.length === 0}>Einladung erneut</button>
            <button className="btn btn-secondary" onClick={() => resend('REMINDER')} disabled={event.attendees.length === 0}>Erinnerung</button>
            <button className="btn btn-danger" onClick={() => resend('CANCEL')} disabled={event.attendees.length === 0}>Absage senden</button>
            <button className="btn btn-secondary" onClick={() => setIngestOpen(true)}>iTIP REPLY einspielen</button>
          </div>
        </div>
        <table className="w-full text-body">
          <thead className="text-meta uppercase tracking-[0.16em] text-ink-muted">
            <tr>
              <th className="text-left py-2">Adressat</th>
              <th className="text-left py-2">Antwort</th>
              <th className="text-left py-2">Antwort am</th>
              <th className="text-right py-2">Sendungen</th>
            </tr>
          </thead>
          <tbody>
            {event.attendees.map((a) => (
              <tr key={a.id} className="border-b border-border/60">
                <td className="py-2">
                  <div>{a.name ?? a.email}</div>
                  <div className="text-small text-ink-muted">{a.email}</div>
                </td>
                <td className="py-2"><Badge tone={rsvpTone(a.rsvp)}>{rsvpLabel(a.rsvp)}</Badge></td>
                <td className="py-2 text-ink-secondary">
                  {a.rsvpAt ? new Date(a.rsvpAt).toLocaleString('de-DE') : '—'}
                </td>
                <td className="py-2 text-right font-mono text-small">
                  {a.sendCount}
                  {a.lastStatus && <span className="ml-2 text-ink-muted">({a.lastStatus.toLowerCase()})</span>}
                </td>
              </tr>
            ))}
            {event.attendees.length === 0 && (
              <tr><td colSpan={4} className="text-ink-muted py-4 text-small">Keine Teilnehmer.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2 className="text-h4 mb-4">Mail-Versandhistorie</h2>
        <table className="w-full text-body">
          <thead className="text-meta uppercase tracking-[0.16em] text-ink-muted">
            <tr>
              <th className="text-left py-2">An</th>
              <th className="text-left py-2">Methode</th>
              <th className="text-left py-2">Status</th>
              <th className="text-left py-2">Zeit</th>
              <th className="text-left py-2">Message-ID</th>
              <th className="text-left py-2">Details</th>
            </tr>
          </thead>
          <tbody>
            {event.deliveries.map((d) => (
              <tr key={d.id} className="border-b border-border/60 align-top">
                <td className="py-2">{d.toEmail}</td>
                <td className="py-2 font-mono text-small">{d.method}</td>
                <td className="py-2"><Badge tone={deliveryTone(d.status)}>{d.status.toLowerCase()}</Badge></td>
                <td className="py-2 text-ink-secondary">
                  {new Date(d.sentAt ?? d.failedAt ?? d.queuedAt).toLocaleString('de-DE')}
                </td>
                <td className="py-2 font-mono text-small text-ink-muted truncate max-w-[180px]">{d.messageId ?? '—'}</td>
                <td className="py-2 text-small text-ink-secondary">
                  {d.errorMessage ? <span className="text-status-danger">{d.errorMessage}</span> : (d.smtpResponse ?? '—')}
                </td>
              </tr>
            ))}
            {event.deliveries.length === 0 && (
              <tr><td colSpan={6} className="text-ink-muted py-4 text-small">Noch nichts versendet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2 className="text-h4 mb-4">Historie</h2>
        <div className="grid gap-2">
          {history.map((item) => (
            <div key={item.id} className="bg-surface-elevated border border-border rounded-md px-3 py-2 flex items-start gap-3">
              <span className="font-mono text-small text-ink-muted min-w-[150px]">{new Date(item.createdAt).toLocaleString('de-DE')}</span>
              <div className="flex-1">
                <div className="font-medium">{item.message}</div>
                <div className="text-small text-ink-muted">{item.action}{item.user ? ` · ${item.user.name ?? item.user.email}` : ''}</div>
              </div>
            </div>
          ))}
          {history.length === 0 && <div className="text-small text-ink-muted">Noch keine Historie.</div>}
        </div>
      </div>

      <Modal open={ingestOpen} onClose={() => setIngestOpen(false)} title="iTIP REPLY einspielen" size="lg" footer={
        <>
          <button className="btn btn-ghost" onClick={() => setIngestOpen(false)}>Abbrechen</button>
          <button className="btn btn-primary" onClick={ingest}>Verarbeiten</button>
        </>
      }>
        <p className="text-small text-ink-secondary mb-3">
          Fuege hier den Inhalt einer .ics-Datei mit <span className="font-mono">METHOD:REPLY</span> ein.
          Hilfreich, solange kein IMAP-Posteingang ueberwacht wird.
        </p>
        <textarea
          className="textarea font-mono"
          rows={14}
          value={ingestText}
          onChange={(e) => setIngestText(e.target.value)}
          placeholder="BEGIN:VCALENDAR&#10;METHOD:REPLY&#10;..."
        />
      </Modal>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Termin bearbeiten" size="lg" footer={
        <>
          <button className="btn btn-ghost" onClick={() => setEditOpen(false)}>Abbrechen</button>
          <button className="btn btn-primary" onClick={saveEdit}>Speichern</button>
        </>
      }>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Titel</label>
            <input className="input" value={editForm.summary} onChange={(e) => setEditForm({ ...editForm, summary: e.target.value })} />
          </div>
          <div>
            <label className="label">Start</label>
            <input type="datetime-local" className="input" value={editForm.startsAt} onChange={(e) => setEditForm({ ...editForm, startsAt: e.target.value })} />
          </div>
          <div>
            <label className="label">Ende</label>
            <input type="datetime-local" className="input" value={editForm.endsAt} onChange={(e) => setEditForm({ ...editForm, endsAt: e.target.value })} />
          </div>
          <label className="flex items-center gap-2 text-body">
            <input type="checkbox" checked={editForm.allDay} onChange={(e) => setEditForm({ ...editForm, allDay: e.target.checked })} />
            Ganztags
          </label>
          <div>
            <label className="label">Status</label>
            <select className="select" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
              <option value="CONFIRMED">Bestaetigt</option>
              <option value="TENTATIVE">Vorlaeufig</option>
              <option value="CANCELLED">Abgesagt</option>
            </select>
          </div>
          <div>
            <label className="label">Zeitzone</label>
            <input className="input" value={editForm.timezone} onChange={(e) => setEditForm({ ...editForm, timezone: e.target.value })} />
          </div>
          <div>
            <label className="label">Wiederholung</label>
            <select className="select" value={editForm.recurrence} onChange={(e) => setEditForm({ ...editForm, recurrence: e.target.value })}>
              <option value="NONE">Keine</option>
              <option value="DAILY">Taeglich</option>
              <option value="WEEKLY">Woechentlich</option>
              <option value="MONTHLY">Monatlich</option>
              <option value="YEARLY">Jaehrlich</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="label">Ort</label>
            <input className="input" value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="label">Beschreibung</label>
            <textarea className="textarea" rows={3} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="label">Teilnehmer</label>
            <textarea className="textarea font-mono" rows={4} value={attendeesInput} onChange={(e) => setAttendeesInput(e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="label">Erinnerungen</label>
            <ReminderPicker value={remindersInput} onChange={setRemindersInput} />
          </div>
          <label className="col-span-2 flex items-center gap-2 text-body">
            <input type="checkbox" checked={editForm.sendInvitations} onChange={(e) => setEditForm({ ...editForm, sendInvitations: e.target.checked })} />
            Aenderung per Mail versenden
          </label>
        </div>
      </Modal>
    </div>
  );
}

function toLocalInput(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function recurrenceToRrule(value: string): string | null {
  if (value === 'DAILY') return 'FREQ=DAILY';
  if (value === 'WEEKLY') return 'FREQ=WEEKLY';
  if (value === 'MONTHLY') return 'FREQ=MONTHLY';
  if (value === 'YEARLY') return 'FREQ=YEARLY';
  return null;
}

function rruleToRecurrence(value?: string | null): string {
  if (!value) return 'NONE';
  if (value.includes('FREQ=DAILY')) return 'DAILY';
  if (value.includes('FREQ=WEEKLY')) return 'WEEKLY';
  if (value.includes('FREQ=MONTHLY')) return 'MONTHLY';
  if (value.includes('FREQ=YEARLY')) return 'YEARLY';
  return 'NONE';
}

function parseAttendees(input: string) {
  return input
    .split(/[\n,;]/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const m = line.match(/^(.+?)\s*<(.+@.+)>$/);
      if (m) return { name: m[1].trim(), email: m[2].trim().toLowerCase() };
      return { email: line.toLowerCase() };
    })
    .filter((a) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email));
}

function ReminderPicker({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const selected = new Set(value.split(',').filter(Boolean));
  const toggle = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(Array.from(next).join(','));
  };
  const options = [
    ['0:NOTIFICATION', 'Zum Start'],
    ['5:NOTIFICATION', '5 Min.'],
    ['15:NOTIFICATION', '15 Min.'],
    ['60:NOTIFICATION', '1 Std.'],
    ['1440:NOTIFICATION', '1 Tag'],
    ['15:EMAIL', '15 Min. Mail'],
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(([key, label]) => (
        <button key={key} type="button" className={`rounded-md border px-3 py-2 text-small ${selected.has(key) ? 'border-brand-primary/50 bg-brand-primary/15 text-ink-primary' : 'border-border bg-surface-elevated text-ink-muted'}`} onClick={() => toggle(key)}>
          {label}
        </button>
      ))}
    </div>
  );
}

function parseReminders(input: string) {
  return input
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => {
      const [minutes, channel] = item.split(':');
      return { minutesBefore: Number(minutes), channel: channel === 'EMAIL' ? 'EMAIL' : 'NOTIFICATION' };
    })
    .filter((item) => Number.isFinite(item.minutesBefore));
}

function remindersToInput(reminders: Reminder[]) {
  return reminders.map((r) => `${r.minutesBefore}:${r.channel}`).join(',');
}

function formatReminder(minutes: number) {
  if (minutes === 0) return 'zum Start';
  if (minutes < 60) return `${minutes} Min. vorher`;
  if (minutes % 1440 === 0) return `${minutes / 1440} Tag(e) vorher`;
  if (minutes % 60 === 0) return `${minutes / 60} Std. vorher`;
  return `${minutes} Min. vorher`;
}
